import MasterCrudPage from "../../components/MasterCrudPage";
import {
  addressMasterApi,
  addressTypeApi,
  locationApi,
  userMasterApi,
} from "../../api/masterApi";

function userLabel(user) {
  const name = [user.first_name, user.last_name].filter(Boolean).join(" ");
  return name || "User";
}

function isType(location, keyword) {
  return String(location.location_type_name || "")
    .toLowerCase()
    .includes(keyword);
}

export default function AddressMaster() {
  return (
    <MasterCrudPage
      title="Address Master"
      description="Add multiple addresses for a user. City, State and Country come from Location Master."
      idKey="address_id"
      api={addressMasterApi}
      listLabel="addresses"
      scopeToCurrentUser
      loadExtraOptions={async () => {
        const [users, types, locations] = await Promise.all([
          userMasterApi.list(),
          addressTypeApi.list(),
          locationApi.list(),
        ]);

        const locationOptions = (locations || []).map((location) => ({
          value: location.location_id,
          label: `${location.location_name}${
            location.location_type_name ? ` (${location.location_type_name})` : ""
          }`,
          location_type_name: location.location_type_name,
          parent_location_id: location.parent_location_id,
        }));

        const cities = locationOptions.filter((location) => isType(location, "city"));
        const states = locationOptions.filter((location) => isType(location, "state"));
        const countries = locationOptions.filter((location) => isType(location, "country"));

        return {
          users: (users || []).map((user) => ({
            value: user.user_id,
            label: userLabel(user),
          })),
          addressTypes: (types || []).map((type) => ({
            value: type.address_type_id,
            label: type.address_type_name,
          })),
          cities: cities.length ? cities : locationOptions,
          states: states.length ? states : locationOptions,
          countries: countries.length ? countries : locationOptions,
        };
      }}
      fields={[
        {
          name: "user_id",
          label: "User",
          type: "select",
          required: true,
          optionsKey: "users",
          placeholder: "Select user",
        },
        {
          name: "address_type_id",
          label: "Address Type",
          type: "select",
          required: true,
          optionsKey: "addressTypes",
          placeholder: "Select address type",
        },
        {
          name: "location_id",
          label: "City (Location)",
          type: "select",
          optionsKey: "cities",
          placeholder: "Select city",
        },
        {
          name: "address_line_1",
          label: "Address Line 1",
          type: "text",
          placeholder: "Street address",
        },
        {
          name: "address_line_2",
          label: "Address Line 2",
          type: "text",
          placeholder: "Apartment, suite, etc.",
        },
        {
          name: "postal_code",
          label: "Postal Code",
          type: "text",
          placeholder: "Postal / ZIP code",
        },
        {
          name: "state_location_id",
          label: "State (Location)",
          type: "select",
          optionsKey: "states",
          placeholder: "Select state",
        },
        {
          name: "country_location_id",
          label: "Country (Location)",
          type: "select",
          optionsKey: "countries",
          placeholder: "Select country",
        },
        {
          name: "address_verified",
          label: "Is Address Verified",
          type: "checkbox",
          defaultValue: false,
          checkLabel: "Yes",
          verificationField: true,
        },
        {
          name: "address_verification_by",
          label: "Address Verified By",
          type: "text",
          placeholder: "Verifier name",
          verificationField: true,
        },
        {
          name: "address_verification_date",
          label: "Address Verification Date",
          type: "date",
          verificationField: true,
        },
        {
          name: "address_verification_certificate",
          label: "Address Verification Certificate",
          type: "image",
          accept: "image/*",
          verificationField: true,
        },
      ]}
      columns={[
        { key: "address_id", label: "Address" },
        {
          key: "user_id",
          label: "User",
          render: (item) =>
            [item.user_id, item.first_name, item.last_name].filter(Boolean).join(" ") || "—",
        },
        { key: "address_type_name", label: "Address Type" },
        { key: "city_name", label: "City" },
        { key: "address_line_1", label: "Address Line 1" },
        { key: "postal_code", label: "Postal Code" },
        { key: "state_location_name", label: "State" },
        { key: "country_location_name", label: "Country" },
        { key: "address_verified", label: "Verified", render: (item) => (Number(item.address_verified) === 1 ? "Yes" : "No") },
        { key: "address_verification_by", label: "Verified By" },
        { key: "address_verification_date", label: "Verified Date" },
      ]}
    />
  );
}
